/**
 * usePostPermissions Hook
 *
 * Checks if current user has permission to create/edit/delete posts
 * Uses Cloud Function checkPostPermission + local role-based defaults
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { functions, db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/types';

// Default permissions by role (matches backend)
const DEFAULT_PERMISSIONS: Record<UserRole, {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canEditAny: boolean;
  canDeleteAny: boolean;
}> = {
  super_admin: {
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canEditAny: true,
    canDeleteAny: true,
  },
  franchise_owner: {
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canEditAny: true,
    canDeleteAny: true,
  },
  admin: {
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canEditAny: true,
    canDeleteAny: true,
  },
  barber: {
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canEditAny: false,
    canDeleteAny: false,
  },
  client: {
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canEditAny: false,
    canDeleteAny: false,
  },
  guest: {
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canEditAny: false,
    canDeleteAny: false,
  },
};

// VIP permissions (can create, edit own, delete own, comment)
const VIP_PERMISSIONS = {
  canCreate: true,
  canEdit: true,
  canDelete: true,
  canEditAny: false,
  canDeleteAny: false,
};

export function usePostPermissions() {
  const { user, firebaseUser, customClaims } = useAuth();
  const [permissions, setPermissions] = useState({
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canEditAny: false,
    canDeleteAny: false,
    loading: true,
  });
  const [isVIP, setIsVIP] = useState(false);

  // Subscribe to VIP status in real-time
  useEffect(() => {
    if (!firebaseUser?.uid) return;

    const userDocRef = doc(db, 'users', firebaseUser.uid);
    const unsubscribe = onSnapshot(userDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const userData = snapshot.data();
        setIsVIP(userData.isVIP || false);
      }
    });

    return () => unsubscribe();
  }, [firebaseUser?.uid]);

  useEffect(() => {
    async function checkPermissions() {
      if (!user || !firebaseUser) {
        setPermissions({
          canCreate: false,
          canEdit: false,
          canDelete: false,
          canEditAny: false,
          canDeleteAny: false,
          loading: false,
        });
        return;
      }

      try {
        // Check if user is VIP (from Firestore or custom claims)
        const userIsVIP = isVIP || customClaims?.isVIP;

        // Start with role-based defaults, or VIP defaults if user is VIP
        let defaults = DEFAULT_PERMISSIONS[user.role];

        // If user is VIP and their role doesn't already have create permission, use VIP permissions
        if (userIsVIP && !defaults.canCreate) {
          defaults = VIP_PERMISSIONS;
        }

        // If user has franchiseId, check for custom permissions
        if (user.franchiseId) {
          try {
            const checkPermission = httpsCallable(functions, 'checkPostPermission');

            // Check all permissions in parallel
            const [createCheck, editCheck, deleteCheck, editAnyCheck, deleteAnyCheck] = await Promise.all([
              checkPermission({
                userId: firebaseUser.uid,
                franchiseId: user.franchiseId,
                action: 'canCreate',
              }),
              checkPermission({
                userId: firebaseUser.uid,
                franchiseId: user.franchiseId,
                action: 'canEdit',
              }),
              checkPermission({
                userId: firebaseUser.uid,
                franchiseId: user.franchiseId,
                action: 'canDelete',
              }),
              checkPermission({
                userId: firebaseUser.uid,
                franchiseId: user.franchiseId,
                action: 'canEditAny',
              }),
              checkPermission({
                userId: firebaseUser.uid,
                franchiseId: user.franchiseId,
                action: 'canDeleteAny',
              }),
            ]);

            setPermissions({
              canCreate: (createCheck.data as any).hasPermission,
              canEdit: (editCheck.data as any).hasPermission,
              canDelete: (deleteCheck.data as any).hasPermission,
              canEditAny: (editAnyCheck.data as any).hasPermission,
              canDeleteAny: (deleteAnyCheck.data as any).hasPermission,
              loading: false,
            });
          } catch (error) {
            console.error('Error checking custom permissions, using defaults:', error);
            // Fallback to defaults if Cloud Function fails
            setPermissions({
              ...defaults,
              loading: false,
            });
          }
        } else {
          // No franchiseId, use defaults
          setPermissions({
            ...defaults,
            loading: false,
          });
        }
      } catch (error) {
        console.error('Error in usePostPermissions:', error);
        setPermissions({
          canCreate: false,
          canEdit: false,
          canDelete: false,
          canEditAny: false,
          canDeleteAny: false,
          loading: false,
        });
      }
    }

    checkPermissions();
  }, [user, firebaseUser, isVIP, customClaims]);

  return permissions;
}

/**
 * Check if user can edit a specific post
 */
export function canEditPost(
  currentUserId: string | undefined,
  postAuthorId: string,
  permissions: ReturnType<typeof usePostPermissions>
): boolean {
  if (!currentUserId || permissions.loading) return false;

  // Can edit if:
  // 1. It's your own post AND you have canEdit permission
  // 2. OR you have canEditAny permission
  return (currentUserId === postAuthorId && permissions.canEdit) || permissions.canEditAny;
}

/**
 * Check if user can delete a specific post
 */
export function canDeletePost(
  currentUserId: string | undefined,
  postAuthorId: string,
  permissions: ReturnType<typeof usePostPermissions>
): boolean {
  if (!currentUserId || permissions.loading) return false;

  // Can delete if:
  // 1. It's your own post AND you have canDelete permission
  // 2. OR you have canDeleteAny permission
  return (currentUserId === postAuthorId && permissions.canDelete) || permissions.canDeleteAny;
}
